/**
 * Forgot password — both web portals.
 *
 *   email ──► a 6-digit code to it ──► the code + a new password ──► signed in
 *
 * The Clerk work is `usePasswordReset` (@tonnie/sdk); the rule is
 * `resetPasswordSchema` (@tonnie/schemas). An account with a second factor
 * cannot finish here and is told so. A visitor already signed in goes to the
 * portal's `afterSignIn`, and so does one this reset just signed in.
 *
 * @see https://clerk.com/docs/guides/development/custom-flows/account-updates/forgot-password
 */

import { useAuth } from '@clerk/clerk-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, CheckCircle2, Loader2 } from '@tonnie/assets';
import {
  useAuthErrorMessage,
  usePasswordRevealLabels,
  usePasswordStrengthLabels,
  useTranslation,
} from '@tonnie/locale';
import { isRefusedCode } from '@tonnie/foundation';
import { useErrorCapture } from '@tonnie/observability';
import {
  emailStepSchema,
  meetsPasswordRule,
  resetPasswordSchema,
  type EmailStepInput,
  type ResetPasswordInput,
} from '@tonnie/schemas';
import { ResetFlowError, usePasswordReset, type PasswordResetOutcome } from '@tonnie/sdk';
import {
  Alert,
  AlertDescription,
  AuthEmailChip,
  Button,
  CodeField,
  Input,
  Label,
  PasswordInput,
  PasswordStrength,
} from '@tonnie/ui';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation } from 'wouter';

import { goToDestination, useAccountPortal } from '../portal';
import { AccountAuthLayout } from './AccountAuthLayout';

export function ForgotPasswordView() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const { afterSignIn } = useAccountPortal();
  const { isLoaded, isSignedIn } = useAuth();
  const flow = usePasswordReset();
  const describeAuthError = useAuthErrorMessage();
  const { captureException } = useErrorCapture();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Already signed in (and not because this reset just did it): nothing to reset.
  useEffect(() => {
    if (isLoaded && isSignedIn && !done) goToDestination(afterSignIn(), setLocation);
  }, [isLoaded, isSignedIn, done, setLocation, afterSignIn]);

  const settle = useCallback(
    (result: PasswordResetOutcome, fallback: string) => {
      switch (result.outcome) {
        case 'next':
          setError(null);
          return;
        case 'complete':
          setError(null);
          setDone(true);
          return;
        case 'failed': {
          if (
            result.error instanceof ResetFlowError &&
            result.error.code === 'second_factor_required'
          ) {
            setError(t('auth.errors.twoFactorRequiredOnReset'));
            return;
          }
          const failure = describeAuthError(result.error, fallback);
          if (!failure.expected) {
            captureException(
              result.error instanceof Error ? result.error : new Error(String(result.error)),
              { page: 'forgot-password', step: flow.state.step }
            );
          }
          setError(failure.message);
          return;
        }
        default: {
          const exhaustive: never = result;
          throw new Error(`Unhandled reset outcome: ${JSON.stringify(exhaustive)}`);
        }
      }
    },
    [t, describeAuthError, captureException, flow.state.step]
  );

  if (done) {
    return (
      <AccountAuthLayout
        brand="sign-in"
        eyebrow={
          <CheckCircle2 className="mb-2 h-10 w-10 text-success-accessible" aria-hidden="true" />
        }
        title={t('auth.passwordResetSuccessTitle')}
        description={t('auth.passwordResetSuccessDesc')}
      >
        <Button
          className="h-11 text-base font-semibold"
          onClick={() => goToDestination(afterSignIn(), setLocation)}
          data-testid="auth-forgot-success"
        >
          {t('auth.flow.continue')}
        </Button>
      </AccountAuthLayout>
    );
  }

  if (flow.state.step === 'email') {
    return (
      <AccountAuthLayout
        brand="sign-in"
        title={t('auth.forgotPasswordTitle')}
        description={t('auth.enterEmailToReset')}
        // The frame's back is the one way back — the page used to add a second,
        // inline "Back to login" link below the form (ACC-06).
        onBack={() => setLocation('/login')}
      >
        <EmailForm
          error={error}
          ready={flow.ready}
          onSubmit={async (email) =>
            settle(await flow.send(email), t('auth.errors.unableToProcess'))
          }
        />
      </AccountAuthLayout>
    );
  }

  return (
    <AccountAuthLayout
      brand="sign-in"
      title={t('auth.createNewPassword')}
      description={
        <AuthEmailChip
          email={flow.state.email}
          changeLabel={t('auth.flow.change')}
          onChange={flow.restart}
        />
      }
      onBack={flow.restart}
    >
      <ResetForm
        email={flow.state.email}
        error={error}
        onSubmit={async (values) => {
          const result = await flow.reset(values);
          settle(result, t('auth.errors.resetFailed'));
          return result.outcome === 'failed' && isRefusedCode(result.error);
        }}
        onResend={async () => settle(await flow.resend(), t('auth.errors.unableToProcess'))}
      />
    </AccountAuthLayout>
  );
}

function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <Alert variant="destructive" data-testid="auth-forgot-error">
      <AlertCircle className="h-4 w-4" aria-hidden="true" />
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}

function EmailForm({
  error,
  ready,
  onSubmit,
}: {
  error: string | null;
  ready: boolean;
  onSubmit: (email: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EmailStepInput>({ resolver: zodResolver(emailStepSchema) });

  return (
    <form
      onSubmit={handleSubmit(({ email }) => onSubmit(email))}
      noValidate
      className="flex flex-col gap-4"
      data-testid="auth-forgot-form"
    >
      <FormError message={error} />
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="forgot-email" className="text-sm font-medium">
          {t('auth.flow.emailLabel')}
        </Label>
        <Input
          id="forgot-email"
          type="email"
          autoComplete="username"
          placeholder={t('auth.flow.emailPlaceholder')}
          className="h-11 text-base"
          aria-invalid={!!errors.email}
          data-testid="auth-forgot-email"
          {...register('email')}
        />
        {errors.email?.message && (
          <p className="text-sm text-destructive-accessible">{t(errors.email.message)}</p>
        )}
      </div>
      <Button
        type="submit"
        className="h-11 text-base font-semibold"
        disabled={isSubmitting || !ready}
        data-testid="auth-forgot-submit"
      >
        {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
        {t('auth.sendResetCode')}
      </Button>
    </form>
  );
}

function ResetForm({
  email,
  error,
  onSubmit,
  onResend,
}: {
  email: string;
  error: string | null;
  /** Resolves `true` when Clerk refused the CODE — the field is then emptied. */
  onSubmit: (values: { code: string; password: string }) => Promise<boolean>;
  onResend: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const reveal = usePasswordRevealLabels();
  const strengthLabels = usePasswordStrengthLabels();
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { code: '', password: '', confirmPassword: '' },
  });
  const password = watch('password');

  return (
    <form
      onSubmit={handleSubmit(async ({ code, password: next }) => {
        // A refused code is emptied so the next goes in whole — over a full
        // field it replaced only the last box (`isRefusedCode`). A refused
        // PASSWORD keeps the code: it may well be right.
        if (await onSubmit({ code, password: next })) setValue('code', '');
      })}
      noValidate
      className="flex flex-col gap-4"
    >
      <FormError message={error} />
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="forgot-code" className="text-sm font-medium">
          {t('auth.resetCode')}
        </Label>
        <Controller
          control={control}
          name="code"
          render={({ field }) => (
            <CodeField
              id="forgot-code"
              value={field.value}
              onChange={field.onChange}
              invalid={!!errors.code}
              autoFocus
              data-testid="auth-forgot-code"
            />
          )}
        />
        {errors.code?.message && (
          <p className="text-sm text-destructive-accessible">{t(errors.code.message)}</p>
        )}
        <button
          type="button"
          onClick={() => void onResend()}
          className="self-start text-sm font-semibold text-primary-accessible hover:underline"
          data-testid="auth-forgot-resend"
        >
          {t('auth.flow.code.resend')}
        </button>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="forgot-password" className="text-sm font-medium">
          {t('auth.newPassword')}
        </Label>
        <PasswordInput
          id="forgot-password"
          revealLabels={reveal}
          autoComplete="new-password"
          className="h-11 text-base"
          aria-invalid={!!errors.password}
          aria-describedby="forgot-password-strength"
          data-testid="auth-forgot-new-password"
          {...register('password')}
        />
        {errors.password?.message && (
          <p className="text-sm text-destructive-accessible">{t(errors.password.message)}</p>
        )}
        <PasswordStrength
          id="forgot-password-strength"
          className="mt-1"
          password={password}
          meetsRule={meetsPasswordRule(password)}
          userInputs={[email]}
          labels={strengthLabels}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="forgot-confirm" className="text-sm font-medium">
          {t('auth.confirmPassword')}
        </Label>
        <PasswordInput
          id="forgot-confirm"
          revealLabels={reveal}
          autoComplete="new-password"
          className="h-11 text-base"
          aria-invalid={!!errors.confirmPassword}
          data-testid="auth-forgot-confirm-password"
          {...register('confirmPassword')}
        />
        {errors.confirmPassword?.message && (
          <p className="text-sm text-destructive-accessible">{t(errors.confirmPassword.message)}</p>
        )}
      </div>
      <Button
        type="submit"
        className="h-11 text-base font-semibold"
        disabled={isSubmitting}
        data-testid="auth-forgot-reset-submit"
      >
        {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
        {t('auth.resetPassword')}
      </Button>
    </form>
  );
}
